import { useLanguage } from '@/hooks/useLanguage';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useState, type ComponentType } from 'react';
import { Facebook } from 'lucide-react';
import { AddPixelModal } from './AddPixelModal';
import type { AddPixelData, Pixel, PixelPlatform } from '@/types/pixel';

/** "♪" marca TikTok (preto + sombra ciano/rosa) sobre círculo escuro */
function TiktokIcon({ className }: { className?: string }) {
  useLanguage();
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#25F4EE"
        d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12v-3.16a5.76 5.76 0 0 0-.77-.05A5.68 5.68 0 1 0 15.54 15V8.36a7.35 7.35 0 0 0 4.3 1.37V6.64a4.26 4.26 0 0 1-3.24-.82z"
      />
      <path
        fill="#FE2C55"
        d="M17.9 5.2a4.25 4.25 0 0 0 1.94-2.2h-3.09a4.28 4.28 0 0 0-1.21-0 6 6 0 0 1 2.36 2.2zM15.54 13.36a2.59 2.59 0 0 0 1.29-2.23V5.82a7.35 7.35 0 0 0 4.3 1.37V4.99a4.26 4.26 0 0 1-3.24-.82l-.01.01v9.95a2.59 2.59 0 0 1-2.59 2.59 2.5 2.5 0 0 1-1.29-.36 2.6 2.6 0 0 0 4.83-1.29z"
      />
      <path
        fill="#fff"
        d="M14.81 3h.73v10.36a2.59 2.59 0 1 1-4.68-1.55v-3.16a5.8 5.8 0 0 0-.77-.05 5.68 5.68 0 1 0 5.67 5.68V5.19a7.38 7.38 0 0 0 4.25 1.4V3.99a4.27 4.27 0 0 1-3.06-1.26 4.24 4.24 0 0 0-2.14.27z"
      />
    </svg>
  );
}

interface Platform {
  id: PixelPlatform;
  name: string;
  description: string;
  available: boolean;
  icon?: ComponentType<{ className?: string }>;
  /** Estilo do círculo do ícone (default: azul HostVSL) */
  iconClassName?: string;
}

/** "G" do Google em cores oficiais (lucide não tem brand icons) */
function GoogleGIcon({ className }: { className?: string }) {
  useLanguage();
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.9-.1-1.5-.3-2.3H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.1.1 3.5 2.7.2.1c2.2-2 3.8-5 3.8-8.7z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.2 0-5.9-2.1-6.8-5l-.1.1-3.6 2.8v.1C3.5 21.4 7.4 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-.1-.1-3.6-2.8-.1.1C.5 8.6 0 10.2 0 12s.5 3.4 1.4 4.9l3.8-2.5z"
      />
      <path
        fill="#EA4335"
        d="M12 4.6c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.4 0 3.5 2.6 1.4 6.8l3.8 2.9c.9-2.9 3.6-5.1 6.8-5.1z"
      />
    </svg>
  );
}

interface ConfiguracaoTabProps {
  videos: Array<{ id_video: string; nomeVideo: string }>;
  addPixel: (data: AddPixelData) => Promise<void>;
  flow?: { id: string; name: string };
  pixels?: Pixel[];
}

export function ConfiguracaoTab({ videos, addPixel, pixels = [], flow }: ConfiguracaoTabProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<PixelPlatform>('facebook');

  const platforms: Platform[] = [
    {
      id: 'facebook',
      name: 'Facebook',
      description: translateInterfaceText("O pixel do Facebook é uma ferramenta de análise que permite medir a eficácia da sua publicidade ao compreender as ações que as pessoas realizam no seu site."),
      available: true,
      icon: Facebook,
    },
    {
      id: 'google-ads',
      name: 'Google Ads',
      description: translateInterfaceText("Meça conversões do Google Ads (ID AW- + rótulo) nos eventos do seu VSL: play, progresso e clique no checkout."),
      available: true,
      icon: GoogleGIcon,
      iconClassName: 'bg-white border border-[#E5E7EB]',
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      description: translateInterfaceText("Meça eventos do TikTok Ads (Pixel Code) no seu VSL: visualização e clique no botão de compra."),
      available: true,
      icon: TiktokIcon,
      iconClassName: 'bg-[#010101]',
    },
  ];

  const platformSummary = (id: PixelPlatform) => {
    const mine = pixels.filter((p) => p.platform === id);
    if (mine.length === 0) return null;
    const firing = mine.filter((p) => (p.health?.last24h || 0) > 0).length;
    if (firing > 0) {
      return { text: `Registrando em ${firing} de ${mine.length} pixel(s)`, cls: 'text-[#16A34A]' };
    }
    const ever = mine.some((p) => (p.health?.total || 0) > 0);
    return ever
      ? { text: `${mine.length} pixel(s) cadastrado(s) — mudo(s) há +24h`, cls: 'text-[#D97706]' }
      : { text: `${mine.length} pixel(s) cadastrado(s) — aguardando 1º disparo`, cls: 'text-[#64748B]' };
  };

  const handleOpenModal = (platform: Platform) => {
    if (platform.available) {
      setSelectedPlatform(platform.id);
      setIsModalOpen(true);
    }
  };

  const handleAddPixel = async (data: AddPixelData) => {
    await addPixel(data);
    setIsModalOpen(false);
  };

  return (
    <div className="max-w-6xl">
      <h2 className="text-lg font-semibold text-[#111] mb-6">{translateInterfaceText("Todas as plataformas")}</h2>
      
      {/* Platform Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {platforms.map((platform) => (
          <div 
            key={platform.id} 
            className={`rounded-[12px] border border-[#E5E7EB] bg-white p-5 transition-all h-full flex flex-col ${
              platform.available ? 'hover:shadow-lg' : 'opacity-50'
            }`}
            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}
          >
            {/* Header: Icon + Title inline */}
            <div className="flex items-center gap-3 mb-3">
              {platform.icon ? (
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    platform.iconClassName ?? 'bg-[#437DFF]'
                  }`}
                >
                  <platform.icon className="w-5 h-5 text-white" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#E5E7EB] flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-[#9CA3AF]">{platform.name[0]}</span>
                </div>
              )}
              <h3 className="text-[15px] font-semibold text-[#111]">
                {platform.name}
              </h3>
            </div>

            {/* Description */}
            <p className="text-[13px] text-[#666] leading-relaxed flex-1 mb-4">
              {platform.description}
            </p>

            {/* Resumo de saúde real (evidência de disparo), não status de "em breve" */}
            {platform.available && (() => {
              const s = platformSummary(platform.id);
              if (!s) {
                return (
                  <p className="text-[12px] text-[#94A3B8] mb-4">
                    {translateInterfaceText("Nenhum pixel")}{" "}{platform.name} {" "}{translateInterfaceText("cadastrado ainda.")}</p>
                );
              }
              return (
                <p className={`text-[12px] font-medium mb-4 ${s.cls}`}>{s.text}</p>
              );
            })()}

            {/* Action - only for available platforms */}
            {platform.available && (
              <button
                onClick={() => handleOpenModal(platform)}
                className="w-full h-[40px] bg-[#3B6FFF] text-white text-[13px] font-medium rounded-[8px]"
              >
                {translateInterfaceText("Configurar Pixel")}</button>
            )}
          </div>
        ))}
      </div>

      {/* Modal */}
      <AddPixelModal
        key={flow?.id || 'videos'}
        flow={flow}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSave={handleAddPixel}
        videos={videos}
        isLoadingVideos={false}
        initialPlatform={selectedPlatform}
      />
    </div>
  );
}
